import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AsyncResource } from 'app/state/resource'
import { CommentFilter, CommentUser } from 'features/collaboration/types'
import { defaultCommentsFilter } from 'features/collaboration/utils/commentUtils'
import { RequestStatus } from 'features/common/types'
import { MapComment, MapCommentResponse, MapCommentWithResponses } from 'features/maps/types'

export interface MapCommentsState {
  commentsOpen: boolean
  commentsVisible: boolean
  commentsFilter: CommentFilter
  comments: MapCommentWithResponses[]
  activeComment?: Partial<MapCommentWithResponses>
  users: CommentUser[]
  commentsSearchResult: AsyncResource<MapComment[]>
}

const initialState: MapCommentsState = {
  commentsOpen: false,
  commentsVisible: true,
  commentsFilter: defaultCommentsFilter,
  comments: [],
  commentsSearchResult: { data: [], status: RequestStatus.Idle },
  activeComment: undefined,
  users: []
}

const reducers = {
  setCommentsOpen: (state: MapCommentsState, action: PayloadAction<boolean>) => {
    state.commentsOpen = action.payload
    if (!action.payload && state.activeComment) {
      state.activeComment = undefined
    }
  },
  setCommentsVisible: (state: MapCommentsState, action: PayloadAction<boolean>) => {
    state.commentsVisible = action.payload
  },
  updateCommentFilter: (state: MapCommentsState, action: PayloadAction<Partial<CommentFilter>>) => {
    state.commentsFilter = { ...state.commentsFilter, ...action.payload }
  },
  setComments: (state: MapCommentsState, action: PayloadAction<MapComment[]>) => {
    state.comments = action.payload.map((c) => ({ ...c, responses: [] }))
    if (state.activeComment?.id) {
      state.activeComment = action.payload.find((c) => c.id === state.activeComment?.id)
    }
  },
  updateComments: (state: MapCommentsState, action: PayloadAction<Partial<MapCommentWithResponses>[]>) => {
    for (const comment of action.payload) {
      const index = state.comments?.findIndex((c) => c.id === comment.id)

      if (index == -1) continue
      state.comments[index] = { ...state.comments[index], ...comment }
    }
  },

  addComment: (state: MapCommentsState, action: PayloadAction<MapComment>) => {
    state.comments?.push({
      ...action.payload,
      responses: []
    })
  },
  updateComment: (state: MapCommentsState, action: PayloadAction<Partial<MapCommentWithResponses>>) => {
    const comment = action.payload
    const index = state.comments?.findIndex((c) => c.id === comment.id)
    if (index === -1) return

    state.comments[index] = { ...state.comments[index], ...comment }

    if (state.activeComment?.id === comment.id) {
      state.activeComment = index !== -1 ? state.comments[index] : comment
    }
  },
  updateCommentResponse: (state: MapCommentsState, action: PayloadAction<Partial<MapCommentResponse>>) => {
    const commentResponse = action.payload
    const commentIndex = state.comments?.findIndex((c) => c.id === commentResponse.commentId)
    if (commentIndex === -1) return

    const comment = state.comments[commentIndex]
    const responseIndex = comment.responses.findIndex((r) => r.id === commentResponse.id)
    if (responseIndex === -1) return

    comment.responses[responseIndex] = { ...comment.responses[responseIndex], ...commentResponse }

    if (state.activeComment?.id === comment.id) {
      state.activeComment = comment
    }
  },
  deleteComment: (state: MapCommentsState, action: PayloadAction<string>) => {
    const commentId = action.payload
    state.comments = state.comments?.filter((c) => c.id !== commentId)
    if (state.activeComment?.id === commentId) {
      state.activeComment = undefined
    }
  },
  updateActiveComment: (
    state: MapCommentsState,
    action: PayloadAction<Partial<MapCommentWithResponses> | undefined>
  ) => {
    state.activeComment = action.payload
      ? {
          ...(action.payload.id === state.activeComment?.id && { ...state.activeComment }),
          ...action.payload
        }
      : undefined
  },
  setCommentUsers: (state: MapCommentsState, action: PayloadAction<CommentUser[]>) => {
    state.users = action.payload
  },
  setCommentsSearchResult: (state: MapCommentsState, action: PayloadAction<AsyncResource<MapComment[]>>) => {
    state.commentsSearchResult = action.payload
  }
}

export const mapCommentsSlice = createSlice({
  name: 'mapComments',
  initialState,
  reducers
})

export const mapCommentsActions = mapCommentsSlice.actions
export const {
  setCommentsOpen,
  setCommentsVisible,
  updateCommentFilter,
  setComments,
  updateComments,
  updateActiveComment,
  addComment,
  updateComment,
  updateCommentResponse,
  deleteComment,
  setCommentUsers,
  setCommentsSearchResult
} = mapCommentsActions

export default mapCommentsSlice.reducer
